import React, { useEffect, useState } from "react";
import { Typography, LinearProgress } from "@mui/material";
import Grid from "@mui/material/Grid";
import { FruitifyContainer } from "../../../shared-components/Containers";
import { useNavigate, useParams } from "react-router-dom";
import { useApiClient } from "../../../api";
import GeneralLoading from "../../../shared-components/Loading";
import StatBox from "../../../shared-components/StatBox";
import { AssignmentSummary } from "../components/AssignmentSummary";
import { OverallFeedback } from "../components/OverallFeedback";
import {
  calculateProgressPercent,
  formatProgressFraction,
  formatWeekDifference,
  getWeekDiffColor,
} from "../../../utils/formatters";
import {
  useExportAssignmentFeedback,
  useExportAssignment,
  useExportCoverSheets,
  useExportResults,
} from "../hooks/Exporters";
import { withPageTracking } from "../../../services/postHog";
import AssignmentCreateNew from "./AssignmentCreateNew";
import { AssignmentQuestionCard } from "../components/AssignmentQuestionCard";

const AssignmentView = () => {
  const { assignmentId } = useParams();
  const ApiClient = useApiClient();
  const { exportAssignment } = useExportAssignment();
  const { exportAssignmentFeedback } = useExportAssignmentFeedback();
  const { exportCoverSheets } = useExportCoverSheets();
  const { exportResults } = useExportResults();
  const [assignment, setAssignment] = useState({});

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAssignment({ initialLoad: true });
  }, [assignmentId]);

  const fetchAssignment = async (initialLoad = false) => {
    setError("");
    if (initialLoad) {
      setLoading(true);
    }
    try {
      const response = await ApiClient.assignment.getAssignment({
        assignment: assignmentId,
      });
      if (response) {
        setAssignment(response.assignment);
      }
    } catch (error) {
      console.error("Failed to fetch assignment", error);
      setError(String(error));
    } finally {
      setLoading(false);
    }
  };
  const handleUploadClicked = () => {
    navigate(`/assignments/submission-hub/${assignment.id}`);
  };

  useEffect(() => {
    if (
      assignment &&
      assignment.isPublished &&
      assignment.assignmentQuestions?.length === 1
    ) {
      navigate(`questions/view/${assignment.assignmentQuestions[0].id}`, {
        replace: true,
      });
    }
  }, [assignment]);

  const handleExportAssignment = async () => {
    await exportAssignment(assignment);
  };

  const handleExportAllFeedback = async () => {
    await exportAssignmentFeedback(assignment);
  };

  const handleDownloadCoverSheets = async () => {
    await exportCoverSheets(assignment);
  };

  const handleExportResults = async () => {
    await exportResults(assignment);
  };

  if (loading) {
    return <GeneralLoading />;
  }

  if (!Object.keys(assignment).length) {
    return <Typography>Unable to load assignment data. {error}</Typography>;
  }

  if (!assignment.isPublished) {
    return (
      <AssignmentCreateNew
        existingAssignment={assignment}
        onPageRefresh={fetchAssignment}
      />
    );
  }

  return (
    <FruitifyContainer>
      <AssignmentSummary
        assignment={assignment}
        handleExportClicked={handleExportAssignment}
        handleExportAllFeedback={handleExportAllFeedback}
        handleDownloadCoverSheets={handleDownloadCoverSheets}
        handleExportResults={handleExportResults}
        setAssignment={setAssignment}
        handleUploadClicked={handleUploadClicked}
        onRefresh={fetchAssignment}
      />
      <Grid container spacing={4} direction={"row"} sx={{ mt: 2, mb: 2 }}>
        <Grid size={6}>
          <StatBox
            title="Overall Submissions"
            stat={formatProgressFraction(assignment.stats.overallSubmissions)}
            footer={
              <LinearProgress
                variant="determinate"
                value={calculateProgressPercent(
                  assignment.stats.overallSubmissions,
                )}
              />
            }
          />
        </Grid>
        <Grid size={6}>
          <StatBox
            title="Average Score"
            stat={
              assignment.stats.averageScore.current
                ? `${assignment.stats.averageScore.current} %`
                : "N/A"
            }
            footer={formatWeekDifference(assignment.stats.averageScore)}
            footerColor={getWeekDiffColor(assignment.stats.averageScore)}
          />
        </Grid>
        {assignment?.assignmentQuestions.map((assignmentQuestion, index) => (
          <Grid size={{ xs: 12, md: 4 }} key={index}>
            <AssignmentQuestionCard
              assignmentQuestion={assignmentQuestion}
              onClick={() =>
                navigate(`questions/view/${assignmentQuestion.id}`)
              }
            />
          </Grid>
        ))}
      </Grid>
      <OverallFeedback assignment={assignment} onRefresh={fetchAssignment} />
    </FruitifyContainer>
  );
};

export default withPageTracking(AssignmentView, {
  feature: "assignments-view",
});
